/**
 * CLI: `canary application page-skill` — review and correct a property's page skills
 *
 * Page skill = a per-page Markdown recipe the fast-drive browser agent reads
 * before acting on a specific page. These are authored by the agent at run time;
 * this CLI is the human audit + correction surface (list/get/versions/set/
 * archive/unarchive), a thin client over the /org/properties/:id/page-skills
 * endpoints.
 *
 * `set` exists because an agent-authored skill can be confidently WRONG, and
 * archiving the whole skill to fix one sentence throws away everything else it
 * observed correctly. Edits append a version, so the prior text stays readable
 * via `versions`.
 */

import { readFileSync } from 'node:fs';
import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest, getApplicationArg } from './cli-helpers.js';

type PageSkillListRow = {
  id: string;
  title: string;
  summary: string;
  tags: string[];
  matchType: 'glob' | 'regex';
  pathPattern: string;
  currentVersion: number;
  bodyBytes: number;
  updatedAt: string;
  archivedAt: string | null;
};

type PageSkill = PageSkillListRow & { body: string; orgId: string; propertyId: string };

type PageSkillVersionRow = {
  version: number;
  title: string;
  summary: string;
  body: string;
  pathPattern: string;
  createdAt: string;
};

type ListResponse = { ok: boolean; error?: string; data?: PageSkillListRow[] };
type GetResponse = { ok: boolean; error?: string; data?: PageSkill };
type VersionsResponse = { ok: boolean; error?: string; data?: PageSkillVersionRow[] };
type OkResponse = { ok: boolean; error?: string; code?: string; actualVersion?: number };
type SetResponse = OkResponse & { data?: PageSkill };

function printHelp(): void {
  console.log(
    [
      "canary application page-skill: Review and correct a property's page skills (per-page recipes authored by the browser agent).",
      '',
      'Sub-commands:',
      '  list      --application <id> [--include-archived] [--pathname </path|url>] [--format json|table]',
      '  get       --application <id> --id <skillId> [--format json|md]',
      '  versions  --application <id> --id <skillId> [--format json|table]',
      '  set       --application <id> --id <skillId> [--body @file|"text"] [--title T] [--summary S] [--tags a,b]',
      '  archive   --application <id> --id <skillId>',
      '  unarchive --application <id> --id <skillId>',
      '',
      'Notes:',
      '  --property is accepted as an alias for --application.',
      '  --pathname filters to the skill(s) that would actually be presented on that',
      '  page, using the same matcher the runtime uses. Accepts a path or a full URL.',
      '  set edits content only and appends a version — the prior text stays readable',
      '  via `versions`. It re-reads the current version and sends it as baseVersion, so',
      '  an agent write landing mid-edit is reported as a conflict instead of clobbered.',
      '  matchType/pathPattern are NOT editable: identity is (application, matchType,',
      '  pathPattern), so changing a pattern would fork a second skill rather than rename.',
      '  Re-target with archive + let the agent author the new pattern.',
      '  archive is a LOSSLESS prune: the skill drops out of the agent surface but',
      '  keeps its body + version history. unarchive restores it without a new version.',
    ].join('\n')
  );
}

/** `@path` reads from a file; anything else is a literal (mirrors `application skill set`). */
function readContent(value: string): string {
  if (value.startsWith('@')) {
    return readFileSync(value.slice(1), 'utf8');
  }
  return value;
}

function requirePropertyId(argv: string[]): string {
  const propertyId = getApplicationArg(argv);
  if (!propertyId) {
    console.error('Error: --application is required (alias: --property).');
    process.exit(1);
  }
  return propertyId;
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const includeArchived = hasFlag(argv, '--include-archived', '--all');
  const format = getArgValue(argv, '--format') ?? (hasFlag(argv, '--json') ? 'json' : 'table');

  const pathname = getArgValue(argv, '--pathname');
  const params = new URLSearchParams();
  if (includeArchived) params.set('includeArchived', '1');
  if (pathname) params.set('pathname', pathname);
  const query = params.toString() ? `?${params.toString()}` : '';
  const result = await apiRequest<ListResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/page-skills${query}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  const rows = result.data ?? [];
  if (format === 'json') {
    console.log(JSON.stringify(rows, null, 2));
    return;
  }

  if (rows.length === 0) {
    console.log(
      pathname
        ? `No page skill matches ${pathname} — the agent gets no skill on that page.`
        : 'No page skills for this property.'
    );
    return;
  }

  for (const row of rows) {
    const flag = row.archivedAt ? ' [archived]' : '';
    console.log(
      `${row.pathPattern}  (v${row.currentVersion}, ${row.bodyBytes}B)${flag}\n` +
        `  id: ${row.id}\n` +
        `  ${row.title} — ${row.summary}`
    );
  }
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const skillId = getArgValue(argv, '--id');
  const format = getArgValue(argv, '--format') ?? (hasFlag(argv, '--json') ? 'json' : 'md');
  if (!skillId) {
    console.error('Error: --id <skillId> is required.');
    process.exit(1);
  }

  const result = await apiRequest<GetResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/page-skills/${skillId}`
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'not found'}`);
    process.exit(1);
  }

  const skill = result.data;
  if (format === 'json') {
    console.log(JSON.stringify(skill, null, 2));
    return;
  }

  console.log(
    `# ${skill.title}\n` +
      `pattern: ${skill.pathPattern} (${skill.matchType})\n` +
      `version: ${skill.currentVersion}${skill.archivedAt ? ' [archived]' : ''}\n` +
      `tags: ${skill.tags.join(', ') || '(none)'}\n\n` +
      `${skill.summary}\n\n---\n${skill.body}`
  );
}

async function handleArchive(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const skillId = getArgValue(argv, '--id');
  if (!skillId) {
    console.error('Error: --id <skillId> is required.');
    process.exit(1);
  }

  const result = await apiRequest<OkResponse>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/page-skills/${skillId}/archive`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  console.log(`Archived page skill ${skillId} (restorable — the agent un-archives it on next upsert).`);
}

async function handleVersions(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const skillId = getArgValue(argv, '--id');
  const format = getArgValue(argv, '--format') ?? (hasFlag(argv, '--json') ? 'json' : 'table');
  if (!skillId) {
    console.error('Error: --id <skillId> is required.');
    process.exit(1);
  }

  const result = await apiRequest<VersionsResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/page-skills/${skillId}/versions`
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'not found'}`);
    process.exit(1);
  }

  if (format === 'json') {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }

  for (const v of result.data) {
    console.log(`v${v.version}  ${v.createdAt}  ${v.body.length}B  ${v.title}`);
  }
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const skillId = getArgValue(argv, '--id');
  if (!skillId) {
    console.error('Error: --id <skillId> is required.');
    process.exit(1);
  }

  const rawBody = getArgValue(argv, '--body');
  const title = getArgValue(argv, '--title');
  const summary = getArgValue(argv, '--summary');
  const rawTags = getArgValue(argv, '--tags');
  if (rawBody === undefined && !title && !summary && rawTags === undefined) {
    console.error('Error: nothing to change — pass at least one of --body, --title, --summary, --tags.');
    process.exit(1);
  }

  // Read the current version so the edit is based on what is live right now. The
  // server rejects the write if an agent lands a version in between, rather than
  // silently overwriting it.
  const current = await apiRequest<GetResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/page-skills/${skillId}`
  );
  if (!current.ok || !current.data) {
    console.error(`Error: ${current.error ?? 'not found'}`);
    process.exit(1);
  }

  const payload: Record<string, unknown> = { baseVersion: current.data.currentVersion };
  if (rawBody !== undefined) payload.body = readContent(rawBody);
  if (title) payload.title = title;
  if (summary) payload.summary = summary;
  if (rawTags !== undefined) {
    payload.tags = rawTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  const result = await apiRequest<SetResponse>(
    apiUrl,
    token,
    'PUT',
    `/org/properties/${propertyId}/page-skills/${skillId}`,
    payload
  );
  if (!result.ok || !result.data) {
    if (result.code === 'PAGE_SKILL_VERSION_CONFLICT') {
      console.error(
        `Error: the skill changed while you were editing (now v${result.actualVersion ?? '?'}). ` +
          're-read it with `get` and re-apply your change.'
      );
      process.exit(1);
    }
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  console.log(
    `Updated ${result.data.pathPattern} → v${result.data.currentVersion} ` +
      `(${result.data.body.length}B). Prior text kept — see \`page-skill versions --id ${skillId}\`.`
  );
}

async function handleUnarchive(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const skillId = getArgValue(argv, '--id');
  if (!skillId) {
    console.error('Error: --id <skillId> is required.');
    process.exit(1);
  }

  const result = await apiRequest<OkResponse>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/page-skills/${skillId}/unarchive`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  console.log(`Restored page skill ${skillId} to the agent surface (no new version).`);
}

export async function runPageSkill(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;

  if (!action || action === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  switch (action) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'versions':
      await handleVersions(rest, apiUrl, token);
      break;
    case 'set':
      await handleSet(rest, apiUrl, token);
      break;
    case 'archive':
      await handleArchive(rest, apiUrl, token);
      break;
    case 'unarchive':
      await handleUnarchive(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown page-skill action: ${action}`);
      printHelp();
      process.exit(1);
  }
}
